import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import KitchenLayout from '../Navigation/KitchenLayout';
import { useAppShell } from '../../contexts/AppShellContext';
import {
  RecipeSearchResponse,
  RecipeSearchResult,
  RecipeSearchSource,
  searchRecipes,
} from '../../services/recipeSearchService';

const PAGE_SIZE = 20;

const sourceLabel: Record<RecipeSearchSource, string> = {
  name: 'Name',
  content: 'Recipe',
  chat: 'Chef chat',
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const HighlightedText: React.FC<{ text: string; terms: string[] }> = ({ text, terms }) => {
  const expression = useMemo(() => {
    if (!terms.length) return null;
    return new RegExp(`(${terms.map(escapeRegExp).sort((left, right) => right.length - left.length).join('|')})`, 'gi');
  }, [terms]);
  if (!expression) return <>{text}</>;
  return <>{text.split(expression).map((part, index) => (
    terms.some(term => part.toLocaleLowerCase() === term.toLocaleLowerCase())
      ? <mark key={`${part}-${index}`} className="rounded-sm bg-amber-100 px-0.5 text-inherit">{part}</mark>
      : <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
  ))}</>;
};

const RecipeSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { skin } = useAppShell();
  const query = searchParams.get('q') || '';
  const normalizedQuery = query.trim();
  const terms = useMemo(
    () => Array.from(new Set(normalizedQuery.split(/\s+/u).filter(Boolean))),
    [normalizedQuery]
  );
  const [response, setResponse] = useState<RecipeSearchResponse>({ results: [], total: 0, nextOffset: null });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef<AbortController | null>(null);

  const load = useCallback(async (term: string, offset: number, append: boolean) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    append ? setLoadingMore(true) : setLoading(true);
    setError(null);
    try {
      const next = await searchRecipes(term, offset, PAGE_SIZE, controller.signal);
      if (controller.signal.aborted) return;
      setResponse(current => append
        ? { ...next, results: [...current.results, ...next.results] }
        : next);
    } catch (reason: any) {
      if (controller.signal.aborted) return;
      setError(reason?.response?.data?.message || reason?.message || 'Could not search recipes');
      if (!append) setResponse({ results: [], total: 0, nextOffset: null });
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    requestRef.current?.abort();
    if (!normalizedQuery) {
      setResponse({ results: [], total: 0, nextOffset: null });
      setLoading(false);
      setLoadingMore(false);
      setError(null);
      return;
    }
    const timer = window.setTimeout(() => void load(normalizedQuery, 0, false), 250);
    return () => {
      window.clearTimeout(timer);
      requestRef.current?.abort();
    };
  }, [load, normalizedQuery]);

  const updateQuery = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value);
    else next.delete('q');
    setSearchParams(next, { replace: true });
  };

  const openResult = (result: RecipeSearchResult) => {
    navigate(`/recipes/${result.id}/edit`, result.matchedSources.includes('chat')
      ? { state: { focusChatInput: true } }
      : undefined);
  };

  const isYolo = skin === 'yolo';

  return (
    <KitchenLayout title="Search" className={isYolo ? 'bg-[#f7f3e8]' : 'bg-gray-50'}>
      <main className={`min-w-0 flex-1 overflow-y-auto ${isYolo ? 'bg-[#f7f3e8]' : 'bg-gray-50'}`}>
        <div className="mx-auto w-full max-w-4xl px-4 py-7 sm:px-7 lg:px-10 lg:py-10">
          <header>
            <h1 className={`text-2xl font-semibold tracking-tight sm:text-3xl ${isYolo ? 'text-stone-900' : 'text-gray-950'}`}>Search recipes</h1>
            <p className={`mt-2 text-sm ${isYolo ? 'text-stone-600' : 'text-gray-600'}`}>
              Find recipes by name, their current instructions, or conversations with Chef.
            </p>
          </header>

          <div className="relative mt-6">
            <svg className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35m2.1-5.4a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={event => updateQuery(event.target.value)}
              placeholder="Search your recipes"
              aria-label="Search your recipes"
              autoFocus
              className={`h-12 w-full rounded-xl border py-2 pl-11 pr-4 text-base shadow-sm outline-none focus:ring-2 ${isYolo
                ? 'border-[#ded2bf] bg-[#fffaf1] text-stone-900 focus:border-amber-500 focus:ring-amber-200'
                : 'border-gray-300 bg-white text-gray-950 focus:border-blue-500 focus:ring-blue-100'}`}
            />
          </div>

          <section className="mt-6" aria-live="polite" aria-busy={loading || loadingMore}>
            {!normalizedQuery && (
              <div className={`rounded-2xl border border-dashed px-6 py-14 text-center ${isYolo ? 'border-[#ded2bf] bg-[#fffaf1] text-stone-500' : 'border-gray-300 bg-white text-gray-500'}`}>
                Type a recipe name, instruction, or something you discussed with Chef.
              </div>
            )}

            {normalizedQuery && loading && (
              <div className="space-y-3" role="status">
                {[0, 1, 2].map(item => <div key={item} className={`h-28 animate-pulse rounded-2xl ${isYolo ? 'bg-[#fffaf1]' : 'bg-white'}`} />)}
                <span className="sr-only">Searching recipes…</span>
              </div>
            )}

            {error && !loading && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                {error}
              </div>
            )}

            {normalizedQuery && !loading && !error && response.results.length === 0 && (
              <div className={`rounded-2xl border px-6 py-14 text-center ${isYolo ? 'border-[#ded2bf] bg-[#fffaf1]' : 'border-gray-200 bg-white'}`}>
                <h2 className={`font-semibold ${isYolo ? 'text-stone-900' : 'text-gray-900'}`}>No matching recipes</h2>
                <p className={`mt-1 text-sm ${isYolo ? 'text-stone-500' : 'text-gray-500'}`}>Try fewer words or a different phrase.</p>
              </div>
            )}

            {!loading && !error && response.results.length > 0 && (
              <>
                <div className="mb-3 text-sm font-medium text-gray-500">
                  {response.total} {response.total === 1 ? 'recipe' : 'recipes'} found
                </div>
                <div className="space-y-3">
                  {response.results.map(result => (
                    <button
                      key={result.id}
                      type="button"
                      onClick={() => openResult(result)}
                      className={`group block w-full rounded-2xl border p-4 text-left shadow-sm transition hover:-translate-y-px hover:shadow-md focus:outline-none focus-visible:ring-2 ${isYolo
                        ? 'border-[#e7dac4] bg-[#fffaf1] focus-visible:ring-amber-500'
                        : 'border-gray-200 bg-white focus-visible:ring-blue-500'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <h2 className={`min-w-0 truncate text-base font-semibold ${isYolo ? 'text-stone-900' : 'text-gray-950'}`}>
                          <HighlightedText text={result.name} terms={terms} />
                        </h2>
                        <svg className="mt-0.5 h-5 w-5 shrink-0 text-gray-400 transition group-hover:translate-x-0.5 group-hover:text-gray-600" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                          <path d="m7 4 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {result.matchedSources.map(source => (
                          <span key={source} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${isYolo ? 'bg-[#f2eadc] text-stone-600' : 'bg-gray-100 text-gray-600'}`}>
                            {sourceLabel[source]}
                          </span>
                        ))}
                      </div>
                      <p className={`mt-3 line-clamp-3 text-sm leading-6 ${isYolo ? 'text-stone-600' : 'text-gray-600'}`}>
                        {result.snippet.source === 'chat' && (
                          <span className="mr-1 font-semibold">{result.snippet.sender === 'user' ? 'You:' : 'Chef:'}</span>
                        )}
                        <HighlightedText text={result.snippet.text} terms={terms} />
                      </p>
                    </button>
                  ))}
                </div>

                {response.nextOffset !== null && (
                  <div className="mt-5 text-center">
                    <button
                      type="button"
                      disabled={loadingMore}
                      onClick={() => void load(normalizedQuery, response.nextOffset!, true)}
                      className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:cursor-wait disabled:opacity-60 ${isYolo
                        ? 'bg-amber-600 text-white hover:bg-amber-700'
                        : 'bg-gray-900 text-white hover:bg-black'}`}
                    >
                      {loadingMore ? 'Loading…' : 'Load more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </main>
    </KitchenLayout>
  );
};

export default RecipeSearchPage;
